'use client';

import { notFound } from 'next/navigation';
import { useState, useCallback } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { Check, ChevronDown, MapPin, Building2, TrendingUp, Calendar, ArrowRight } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { CITY_CONTENT } from '@/core/i18n/content/cities';
import type { CityLandingContent } from '@/core/i18n/content/cities';
import type { CityLandingConfig } from './seo-cities.data';

export type { CityLandingConfig } from './seo-cities.data';

/* -------------------------------------------------------------------------- */
/*  Section -- Hero                                                           */
/* -------------------------------------------------------------------------- */

function CityHero({ config, t }: { config: CityLandingConfig; t: TemplateChromeContent }) {
  const pfx = 'city-landing';
  return (
    <section id={`${pfx}-hero`} className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <div className="mb-4 flex items-center justify-center gap-2 text-sm text-hms-textSub">
          <MapPin className="h-4 w-4 text-hms-gold" />
          <span>{config.cityName}, {config.country}</span>
        </div>
        <h1
          id={`${pfx}-title`}
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {config.heroTitle}
        </h1>
        <p id={`${pfx}-subtitle`} className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {config.heroSubtitle}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`${pfx}-cta-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- KPI Stats                                                      */
/* -------------------------------------------------------------------------- */

function CityStats({ config, t }: { config: CityLandingConfig; t: TemplateChromeContent }) {
  const pfx = 'city-landing';
  const items = [
    { value: config.stats.hotelCount, label: t.seoTemplate.hotels, icon: Building2 },
    { value: config.stats.avgRate, label: t.seoTemplate.avgRate, icon: TrendingUp },
    { value: config.stats.occupancyRate, label: t.seoTemplate.occupancy, icon: TrendingUp },
    { value: config.stats.peakSeason, label: t.seoTemplate.peakSeason, icon: Calendar },
  ];
  return (
    <section id={`${pfx}-stats`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
          {items.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div
                key={idx}
                id={`${pfx}-stat-${idx}`}
                className="rounded-xl border border-hms-border bg-hms-card p-6 text-center"
              >
                <div className="mb-3 flex justify-center">
                  <Icon className="h-5 w-5 text-hms-gold" />
                </div>
                <p
                  className={cn(
                    'text-2xl font-extrabold tracking-tight sm:text-3xl',
                    'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
                  )}
                >
                  {stat.value}
                </p>
                <p className="mt-2 text-sm font-semibold text-hms-textSub">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Highlights                                                     */
/* -------------------------------------------------------------------------- */

function CityHighlights({ config, t }: { config: CityLandingConfig; t: TemplateChromeContent }) {
  const pfx = 'city-landing';
  return (
    <section id={`${pfx}-highlights`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-highlights-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.seoTemplate.whyHotelsChoose.replace('{0}', config.cityName)}
        </h2>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {config.highlights.map((item, idx) => (
            <div
              key={idx}
              id={`${pfx}-highlight-${idx}`}
              className={cn(
                'group rounded-xl border border-hms-border bg-hms-card p-6',
                'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
              )}
            >
              <h3 className="mb-2 text-base font-bold tracking-tight text-hms-text">{item.title}</h3>
              <p className="text-sm leading-relaxed text-hms-textSub">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Property Types                                                 */
/* -------------------------------------------------------------------------- */

function CityPropertyTypes({ config, t }: { config: CityLandingConfig; t: TemplateChromeContent }) {
  const pfx = 'city-landing';
  return (
    <section id={`${pfx}-property-types`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-property-types-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {t.seoTemplate.propertyTypesServe.replace('{0}', config.cityName)}
        </h2>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {config.propertyTypes.map((type, idx) => (
            <span
              key={idx}
              id={`${pfx}-property-type-${idx}`}
              className="rounded-full border border-hms-border bg-hms-card px-5 py-2.5 text-sm font-medium text-hms-text transition-colors hover:border-hms-gold/30 hover:text-hms-gold"
            >
              {type}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Popular Areas                                                  */
/* -------------------------------------------------------------------------- */

function CityPopularAreas({ config, t }: { config: CityLandingConfig; t: TemplateChromeContent }) {
  const pfx = 'city-landing';
  return (
    <section id={`${pfx}-popular-areas`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-popular-areas-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {t.seoTemplate.popularAreas.replace('{0}', config.cityName)}
        </h2>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {config.popularAreas.map((area, idx) => (
            <span
              key={idx}
              id={`${pfx}-area-${idx}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-hms-border bg-hms-card px-5 py-2.5 text-sm font-medium text-hms-text transition-colors hover:border-hms-gold/30 hover:text-hms-gold"
            >
              <MapPin className="h-3.5 w-3.5 text-hms-gold" />
              {area}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- Compliance                                                     */
/* -------------------------------------------------------------------------- */

function CityCompliance({ config, t }: { config: CityLandingConfig; t: TemplateChromeContent }) {
  const pfx = 'city-landing';
  return (
    <section id={`${pfx}-compliance`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-compliance-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.seoTemplate.localCompliance}
        </h2>
        <div className="mt-12 space-y-4">
          {config.compliance.map((item, idx) => (
            <div
              key={idx}
              id={`${pfx}-compliance-item-${idx}`}
              className="flex items-start gap-3 rounded-xl border border-hms-border bg-hms-card p-4"
            >
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hms-green/10">
                <Check className="h-3.5 w-3.5 text-hms-green" />
              </div>
              <span className="text-sm font-medium leading-relaxed text-hms-text">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- FAQ                                                            */
/* -------------------------------------------------------------------------- */

function CityFAQ({ config, t }: { config: CityLandingConfig; t: TemplateChromeContent }) {
  const pfx = 'city-landing';
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id={`${pfx}-faq`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-faq-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.faq}
        </h2>
        <div className="mt-12 space-y-3">
          {config.faqItems.map((item, idx) => (
            <div
              key={idx}
              id={`${pfx}-faq-item-${idx}`}
              className={cn(
                'rounded-xl border transition-colors',
                openIndex === idx
                  ? 'border-hms-gold/30 bg-hms-card'
                  : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
              )}
            >
              <button
                id={`${pfx}-faq-toggle-${idx}`}
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <span
                  className={cn(
                    'text-sm font-semibold leading-snug',
                    openIndex === idx ? 'text-hms-gold' : 'text-hms-text',
                  )}
                >
                  {item.question}
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 transition-transform duration-200',
                    openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim',
                  )}
                />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section -- CTA                                                            */
/* -------------------------------------------------------------------------- */

function CityCTA({ config, t }: { config: CityLandingConfig; t: TemplateChromeContent }) {
  const pfx = 'city-landing';
  return (
    <section id={`${pfx}-cta`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-cta-title`}
          className={cn(
            'text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.seoTemplate.readyToModernize.replace('{0}', config.cityName)}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">
          {t.seoTemplate.joinHundredsCity.replace('{0}', config.cityName)}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`${pfx}-cta-final-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`${pfx}-cta-final-contact`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">
          {t.common.noCreditCard}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function CityLanding({ slug }: { slug: string }) {
  const t = usePageContent(TEMPLATE_CHROME);

  // A slug with no content record renders 404, not a crash.
  //
  // Same shape as the IntegrationLanding defect: the `!` asserted the lookup
  // always succeeds, and when it did not, `record.content` threw
  // "Cannot read properties of undefined (reading 'content')" — which the route
  // error boundary turned into a 500 for every locale.
  //
  // All 11 city pages DO currently have records, so this is not fixing a live
  // break; it removes the way the next added page would become a 500 instead
  // of a 404. A non-null assertion cannot make a missing key present — it only
  // hides the warning that would have caught it.
  const record = CITY_CONTENT[slug];
  if (!record) notFound();

  const localized = usePageContent(record.content);

  const config: CityLandingConfig = {
    slug: record.constants.slug,
    cityName: localized.cityName,
    country: localized.country,
    heroTitle: localized.heroTitle,
    heroSubtitle: localized.heroSubtitle,
    stats: record.constants.stats,
    highlights: localized.highlights,
    propertyTypes: localized.propertyTypes,
    popularAreas: localized.popularAreas,
    compliance: localized.compliance,
    faqItems: localized.faqItems,
  };

  return (
    <main id={`city-landing-${config.slug}-page`}>
      <CityHero config={config} t={t} />
      <CityStats config={config} t={t} />
      <CityHighlights config={config} t={t} />
      <CityPropertyTypes config={config} t={t} />
      <CityPopularAreas config={config} t={t} />
      <CityCompliance config={config} t={t} />
      <CityFAQ config={config} t={t} />
      <CityCTA config={config} t={t} />
    </main>
  );
}
